import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { API, handleError, postRequestConfig } from './api';
import UserSettingsContext from './UserSettingsContext';
import './form.css';
import FormTextInput from './FormTextInput';

export const emailPattern = /^([a-zA-Z0-9_\-+.]+)@([a-zA-Z0-9_\-.]+).([a-zA-Z]{2,5})$/;

export function validateEmail(value) {
  if (!value) {
    return undefined;
  }
  return !!value.match(emailPattern);
}

export function validatePassword(value) {
  if (!value) {
    return undefined;
  }
  return value.length >= 8;
}

function Register(props) {
  const { t } = useTranslation('translations');

  const history = useHistory();
  const location = useLocation();
  const { userSettings } = useContext(UserSettingsContext);

  const { state = {} } = location;

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(undefined);
  const [marketing, setMarketing] = useState(props.marketing || false);

  const nameRef = useRef(true);
  const emailRef = useRef(true);
  const passwordRef = useRef(true);

  const [nameValid, setNameValid] = useState(undefined);
  const [emailValid, setEmailValid] = useState(undefined);
  const [passwordValid, setPasswordValid] = useState(undefined);

  function getNameValidation(value) {
    setError(undefined);
    const valid = value.length > 0;
    setNameValid(valid);
    return valid ? undefined : t('Name is missing.');
  }
  
    
  function getEmailValidation(value) {
    setError(undefined);
    const valid = validateEmail(value);
    setEmailValid(valid);
    return valid ? undefined : t('Please check the spelling of your email address.');
  }
  
  function getPasswordValidation(value) {
    setError(undefined);
    const valid = validatePassword(value);
    setPasswordValid(valid);
    return valid ? undefined : t('Password must be at least 8 characters long.');
  }
  
  const active = useRef(true);
  useEffect(() => {
    return () => {
      active.current = false;
    } 
  }, []);

  const { redirect } = userSettings;
  const {
    name = '',
    email = state.email || userSettings.email || '',
    password = '',
  } = props;

  function processForm(event) {
    event.preventDefault();
    if (processing) {
      return;
    }
    setProcessing(true);
    const emailValue = emailRef.current.value;
    fetch(`${API}/signup/email`, {
      ...postRequestConfig,
      body: `name=${
        encodeURIComponent(nameRef.current.value)
      }&address=${
        encodeURIComponent(emailValue)
      }&password=${
        encodeURIComponent(passwordRef.current.value)
      }&marketing=${
        encodeURIComponent(marketing)
      }${
        redirect
        ? `&redirect=${encodeURIComponent(redirect)}`
        : ''
      }`,
    })
    .then(handleError)
    .then(() => {
      if (!active.current) {
        // We have been unmounted already
        return;
      }
      setProcessing(false);
      history.push({ pathname: '/verify', state: { email: emailValue, verify: 'email' }});
    })
    .catch((error = {}) => {
      if (!active.current) {
        // We have been unmounted already
        return;
      }
      console.error(error);
      let errorMessage;
      const { status } = error.response;
      switch (+status) {
        case 429:
          errorMessage =
            t('We have sent five messages to confirm your email address. Please contact us by support@sensotrend.com to resolve the issue.');
          break;
        case 404:
        case 500:
        case 501:
        case 502:
        case 503:
          errorMessage =
            t('The service required to register accounts is under maintenance. Please wait a moment and then try again! If the issue persists, you can contact us by support@sensotrend.com.');
          break;
        default:
          errorMessage =
          t('Creating the account failed. Please check your internet connection and try again!');
      }
      setError(errorMessage);
      setProcessing(false);
    });
  }

  return (
    <Fragment>
      <header>
        <h1>{t('Create a new account!')}</h1>
      </header>
      <main>
        <form onSubmit={processForm}>
          <FormTextInput
            ref={nameRef}
            label={t('Name')}
            id="name"
            type="text"
            name="name"
            defaultValue={name}
            required
            autoFocus
            title={t('Your name is visible in the user interface.')}
            pattern=".{1,}"
            placeholder=""
            getValidationError={getNameValidation}
          />
          <FormTextInput
            ref={emailRef}
            label={t('Email address')}
            id="email"
            type="email"
            name="email"
            defaultValue={email}
            required
            title={t('Your email address acts as a username. In addition, we will send you important service-related notifications via email.')}
            placeholder=""
            getValidationError={getEmailValidation}
          />
          <FormTextInput
            ref={passwordRef}
            label={t('Password')}
            id="password"
            type="password"
            name="new-password"
            defaultValue={password}
            required
            title={t('Please create a unique password for this service.')}
            pattern=".{8,}"
            placeholder=""
            getValidationError={getPasswordValidation}
          />
          <section>
            <p>
              {t('If you wish, we will send you information about the development of the service and the opportunities to participate in the development.')}
            </p>
            <div>
              <input
                id="marketing"
                type="checkbox"
                name="marketing"
                title={t('If you wish, we will send you information about the development of the service and the opportunities to participate in the development.')}
                checked={marketing}
                onChange={(event) => setMarketing(event.target.checked)}
              />
              <label htmlFor="marketing">{t('I want messages about service development.')}</label>
            </div>
          </section>
          <input
            type="submit"
            title={t('Create an account.')}
            disabled={processing || !nameValid || !emailValid || !passwordValid}
            value="OK"
          />
        </form>
        {error
          ? <p className="error">{error}</p>
          : undefined
        }
      </main>
    </Fragment>
  );
}

export default Register;
